import { decamelize, decamelizeKeys } from 'humps';
import _ from 'lodash';

import { TrackerInstance } from '../types/Tracker';

import { baseApi } from './baseApi';
import { patientApi } from './patientApi';
import { surveyApi } from './surveyApi';

function buildFormData(params: Record<string, unknown>): FormData {
  const formData = new FormData();
  for (const key in params) {
    if (_.has(params, key)) {
      const value = params[key];
      if (value !== null && value !== undefined) {
        formData.append(decamelize(key), value as string);
      }
    }
  }
  return formData;
}

const trackerApi = baseApi
  .enhanceEndpoints({ addTagTypes: [
    'Patient',
    'Survey',
    'TrackerInstance',
    'TrackerInstances',
  ]})
  .injectEndpoints({
    endpoints: (builder) => ({
      postTracker: builder.mutation({
        query: (params) => ({
          url: 'api/v5/tracker',
          method: 'POST',
          body: buildFormData(params),
          timeout: 60000,
        }),
        async onQueryStarted({ applicationEventId, createdAt, eventType, procedureId }, { dispatch, queryFulfilled }) {
          // optimistically update patient completed survey
          const patchResult = dispatch(
            patientApi.util.updateQueryData('getPatient', undefined, (draft) => {
              const survey = _.find(draft.surveys, ['id', procedureId]);

              if (survey) {
                survey.lastEntryDate = createdAt;
              }

              if (eventType === 'setup_survey') {
                draft.setupSurveyCompleted = true;
              }

              if (eventType === 'main_survey') {
                draft.endSurveyCompleted = true;
              }

              if (applicationEventId) {
                draft.completedEvents.push({
                  applicationEventId,
                  createdAt: new Date().toISOString(),
                });
              }
            }),
          );
          const surveyPatchResult = dispatch(
            surveyApi.util.updateQueryData('getSurvey', procedureId, (draft) => {
              draft.lastEntryDate = createdAt;
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            surveyPatchResult.undo();
          }
        },
        invalidatesTags: (_result, _error, arg) => ([
          'Patient',
          { type: 'Survey', id: arg.procedureId },
          { type: 'TrackerInstance', id: arg.procedureId },
          { type: 'TrackerInstances', id: 'LIST' },
        ]),
      }),
      archiveTracker: builder.mutation({
        query: (params) => {

          return {
            url: 'api/v5/tracker/archive',
            method: 'PATCH',
            body: params,
            timeout: 60000,
          };
        },
        invalidatesTags: (_result, _error, arg) => ([
          { type: 'TrackerInstance', id: arg.procedureId },
          { type: 'TrackerInstances', id: 'LIST' },
        ]),
      }),
      getTrackerInstances: builder.query<TrackerInstance[], number | null>({
        query: (id = null) => {
          const params = id ? `?procedure_id=${id}` : '';
          return `api/v5/tracker_instances${params}`;
        },
        providesTags: (_result, _error, id) => 
          id 
            ? [{ type: 'TrackerInstance', id: id }, { type: 'TrackerInstances', id: 'LIST' }]
            : [{ type: 'TrackerInstances', id: 'LIST' }],
      }),
      getLastTracker: builder.query<TrackerInstance, number>({
        query: (id) => `api/v5/last_tracker?procedure_id=${id}`,
        providesTags: (_result, _error, arg) => ([{ type: 'TrackerInstance', id: arg }]),
      }),
      saveTrackerDraft: builder.mutation({
        query: (params) => ({
          url: 'api/v4/tracker_drafts/update',
          method: 'POST',
          body: buildFormData(params),
          timeout: 60000,
        }),
        async onQueryStarted({ applicationEventId, procedureId }, { dispatch, queryFulfilled }) {
          // optimistically update timestamp on patient survey drafts
          const patchResult = dispatch(
            patientApi.util.updateQueryData('getPatient', undefined, (draft) => {
              const entry = {
                applicationEventId,
                procedureId,
              };

              const draftCreatedAt = _.now();
              const cachedEntry = _.find(draft.surveyDrafts, ['procedureId', procedureId]);

              if (cachedEntry) {
                cachedEntry.draftCreatedAt = draftCreatedAt;
              } else {
                draft.surveyDrafts.push({ ...entry, draftCreatedAt });
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            try {
              patchResult.undo();
            } catch {
              console.error('Failed to undo optimistic update for saveTrackerDraft');
            }
          }
        },
        invalidatesTags: (_result, _error, arg) => ([{ type: 'Survey', id: arg.procedureId }]),
      }),
      deleteTrackerDraft: builder.mutation({
        query: (params) => {
          return {
            url: 'api/v4/tracker_drafts/delete',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
        async onQueryStarted({ procedureId }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            patientApi.util.updateQueryData('getPatient', undefined, (draft) => {
              // optimistically remove draft entry from patient survey drafts
              _.remove(draft.surveyDrafts, (entry) => entry.procedureId === procedureId);
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            try {
              patchResult.undo();
            } catch {
              console.error('Failed to undo optimistic update for deleteTrackerDraft');
            }
          }
        },
      }),
    }),
    overrideExisting: false,
  });

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  usePostTrackerMutation,
  useArchiveTrackerMutation,
  useGetTrackerInstancesQuery,
  useGetLastTrackerQuery,
  useLazyGetLastTrackerQuery,
  useSaveTrackerDraftMutation,
  useDeleteTrackerDraftMutation,
} = trackerApi;
